import React, { useEffect, useState, useRef } from 'react';
import { makeStyles, Button } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/HighlightOff';
import { useTranslation } from 'react-i18next';

import Search from '../../Search';
import SearchableChecklistItem, { ISearchableChecklistItem } from './SearchableChecklistItem';
import useSearchableChecklist from './useSearchableChecklist';
import ListError from './ListError';
import ListLoading from './ListLoading';
import ListNoData from './ListNoData';

export interface IFilter {
    items: ISearchableChecklistItem[];
    searchName?: string;
    onSubmit: (selected: ISearchableChecklistItem[], changed: ISearchableChecklistItem[]) => void;
    onChange: (selected: ISearchableChecklistItem[]) => void;
    onClear: () => void;
    loading?: boolean;
    loadingMessage?: React.ReactNode;
    errorMessage?: React.ReactNode;
    noDataMessage?: React.ReactNode;
    error?: boolean;
    isZeroAcceptable?: boolean;
    resetSelectedFilters?: boolean;
}

const Filter = (props: IFilter) => {
    const useStyles = makeStyles({
        root: {
            width: '200px',
            borderRadius: '16px',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
            position: 'relative',
            zIndex: 999,
            marginTop: '0.5rem',
            float: 'left',
            marginRight: '0.5rem',
        },
        constainer: {
            position: 'absolute',
            bottom: '0',
            left: '0',
            transform: 'translateY(100%)',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 1rem #45568130',
            borderRadius: '8px',
            overflow: 'hidden',
        },
        header: {
            borderBottom: '1px solid #E8EAF4',
            backgroundColor: '#FFFFFF',
        },
        body: {
            height: '12.5rem',
            overflowY: 'scroll',
            overflowX: 'hidden',
            backgroundColor: '#FFFFFF',
            zIndex: 999,
            '@media only screen and (max-height: 800px)': {
                height: '6.25rem',
            },
            '@media only screen and (max-width: 767px)': {
                height: '6.25rem',
            },
        },
        footer: {
            borderTop: '1px solid #E8EAF4',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '1rem',
            color: '#455681',
            padding: '0.5rem',
        },
        button: {
            color: '#737FA4',
            backgroundColor: '#F3F4F9',

            '&.active': {
                color: '#ffffff',
                backgroundColor: '#455681',
            },
        },
        buttonIcon: {
            color: '#737FA4',

            '&.active': {
                color: '#ffffff',
            },
        },
        clear: {
            display: 'flex',
            paddingLeft: '1rem',
            cursor: 'pointer',

            '& span': {
                marginLeft: '0.5rem',
            },
        },
        content: {
            whiteSpace: 'nowrap',
            overflow: ' hidden !important',
            textOverflow: 'ellipsis',
            width: '100px',
        },
    });

    const classes = useStyles();
    const { t } = useTranslation();
    const { currentList, setCurrentList, getSelected, selectNone, onSearchChange, onCheckboxChange, filteredList } =
        useSearchableChecklist(props.items);

    const [showFilter, setShowFilter] = useState<Boolean>(false);
    const [serachValue, setSerachValue] = useState<string>('');

    const onChange = () => {
        props.onChange(getSelected());
    };

    useEffect(() => {
        selectNone();
        props.onClear();
        // eslint-disable-next-line
    }, [props.resetSelectedFilters]);

    useEffect(() => {
        if (filteredList === null) {
            props.onChange(getSelected());
        }
        // eslint-disable-next-line
    }, [currentList]);

    useEffect(() => {
        if (props.error || props.loading) {
            selectNone();
        }
        //eslint-disable-next-line
    }, [props.error, props.loading]);

    useEffect(() => {
        onSearchChange(serachValue);
        //eslint-disable-next-line
    }, [serachValue]);

    useEffect(() => {
        setCurrentList(props.items);
    }, [props.items, setCurrentList]);

    const wrapperRef = useRef(null);
    useOutsideClicker(wrapperRef, () => {
        setShowFilter(false);
    });

    const renderItems = () => {
        if (props.error) return props.errorMessage || <ListError message={props.errorMessage} />;
        if (props.loading) return props.loadingMessage || <ListLoading />;
        if (currentList.length < 1) return props.noDataMessage || <ListNoData message={props.noDataMessage} />;
        if (serachValue.length > 0 && filteredList !== null) {
            return filteredList.map((item: ISearchableChecklistItem, i: number) => (
                <SearchableChecklistItem
                    //@ts-ignore
                    content={item.content ?? item.name_public}
                    contentClassName={classes.content}
                    {...item}
                    index={i}
                    onCheckboxChange={(checked, index) => {
                        onCheckboxChange(checked, index);

                        onChange();
                    }}
                    hasFrame={false}
                />
            ));
        }
        return currentList.map((item: ISearchableChecklistItem, i: number) => (
            <SearchableChecklistItem
                //@ts-ignore
                content={item.content ?? item.name_public}
                contentClassName={classes.content}
                {...item}
                index={i}
                onCheckboxChange={(checked, index) => {
                    onCheckboxChange(checked, index);

                    onChange();
                }}
                hasFrame={false}
            />
        ));
    };

    return (
        <div className={classes.root} ref={wrapperRef}>
            <Button
                variant="contained"
                className={`${classes.button} ${showFilter && 'active'}`}
                onClick={() => {
                    setShowFilter(!showFilter);
                }}
                endIcon={
                    showFilter ? (
                        <ExpandLess className={`${classes.buttonIcon} ${showFilter && 'active'}`} />
                    ) : (
                        <ExpandMore className={`${classes.buttonIcon} ${showFilter && 'active'}`} />
                    )
                }
            >
                {props.searchName}
            </Button>

            {showFilter && (
                <div className={classes.constainer}>
                    <div className={classes.header}>
                        {
                            <Search
                                onChange={(e) => setSerachValue(e.target.value)}
                                hasBorder={false}
                                searchFields={[{ id: '1', displayName: props.searchName ?? 'Items' }]}
                                placeholderOverride="Search"
                                value={serachValue}
                            />
                        }
                    </div>

                    <div className={classes.body}>{renderItems()}</div>

                    <div className={classes.footer}>
                        <div className={classes.clear}>
                            <ClearIcon />

                            <span
                                onClick={() => {
                                    selectNone();

                                    props.onClear();
                                }}
                            >
                                {t('filters.clearSelection')}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

Filter.defaultProps = {
    isZeroAcceptable: true,
    searchName: 'Items',
} as Partial<IFilter>;

export default Filter;

function useOutsideClicker(ref: any, callback: Function) {
    useEffect(() => {
        function handleClickOutside(event: any) {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
}
